import { hasCsdAnalyticsConsent } from './privacyConsent';

const TRACKING_SESSION_KEY = 'csd:natura-visitor-tracked:v2';
const SESSION_ID_KEY = 'csd:session-id';
const ATTRIBUTION_KEY = 'csd:natura-attribution:v1';

const NATURA_BASE_URL = 'https://shrmmrxqqrsevzevjngw.supabase.co/functions/v1';
const NATURA_TRACKING_ENDPOINT = `${NATURA_BASE_URL}/track-click`;
const NATURA_UX_ENDPOINT = `${NATURA_BASE_URL}/track-ux-event`;
export const NATURA_TRACKING_CAMPAIGN_ID = '7b8786f7-f3fb-420e-8bd2-9afb05fc166b';

type SafeMetadataValue = string | number | boolean | null;
type BusinessMetadata = Record<string, SafeMetadataValue | undefined>;

const protectedPath = (path: string) =>
  /^\/(admin|account|auth|callback)(\/|$)/i.test(path) ||
  /(?:token|code|email|password|session)/i.test(path);

const normalizedPath = (path: string) => path.split('?')[0].split('#')[0].slice(0, 240);

const safeUrl = (value: string) => {
  if (!value) return '';
  try {
    const url = new URL(value, window.location.origin);
    url.search = '';
    url.hash = '';
    return url.toString().slice(0, 1000);
  } catch {
    return '';
  }
};

const getSessionId = () => {
  try {
    const existing = sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const generated =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `csd_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_ID_KEY, generated);
    return generated;
  } catch {
    return `csd_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }
};

const readAttribution = () => {
  const params = new URLSearchParams(window.location.search);
  const current = {
    utm_source: (params.get('utm_source') || '').slice(0, 180),
    utm_medium: (params.get('utm_medium') || '').slice(0, 180),
    utm_campaign: (params.get('utm_campaign') || '').slice(0, 180),
    utm_content: (params.get('utm_content') || '').slice(0, 180),
    utm_term: (params.get('utm_term') || '').slice(0, 180),
  };

  const hasCurrent = Object.values(current).some(Boolean);
  if (hasCurrent) {
    try {
      sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(current));
    } catch {
      // Best-effort analytics storage only.
    }
    return current;
  }

  try {
    const stored = JSON.parse(sessionStorage.getItem(ATTRIBUTION_KEY) || '{}');
    return {
      utm_source: typeof stored.utm_source === 'string' ? stored.utm_source.slice(0, 180) : '',
      utm_medium: typeof stored.utm_medium === 'string' ? stored.utm_medium.slice(0, 180) : '',
      utm_campaign: typeof stored.utm_campaign === 'string' ? stored.utm_campaign.slice(0, 180) : '',
      utm_content: typeof stored.utm_content === 'string' ? stored.utm_content.slice(0, 180) : '',
      utm_term: typeof stored.utm_term === 'string' ? stored.utm_term.slice(0, 180) : '',
    };
  } catch {
    return current;
  }
};

const getReferrerHost = () => {
  try {
    return document.referrer ? new URL(document.referrer).hostname.slice(0, 255) : '';
  } catch {
    return '';
  }
};

const resolveSourceType = (utmSource: string) => {
  if (utmSource) return 'utm';
  if (document.referrer) return 'referral';
  return 'direct';
};

const sanitizeMetadata = (metadata: BusinessMetadata) => {
  const blockedKeys = new Set([
    'email',
    'name',
    'first_name',
    'last_name',
    'phone',
    'address',
    'message',
    'description',
  ]);

  return Object.fromEntries(
    Object.entries(metadata)
      .filter(([key, value]) => !blockedKeys.has(key) && value !== undefined)
      .slice(0, 28)
      .map(([key, value]) => [
        key.slice(0, 80),
        typeof value === 'string' ? value.slice(0, 260) : value,
      ])
  );
};

export const trackNaturaVisitor = (path: string) => {
  if (typeof window === 'undefined' || !hasCsdAnalyticsConsent()) return;

  const pathname = normalizedPath(path);
  if (!pathname.startsWith('/') || protectedPath(pathname)) return;

  try {
    const state = sessionStorage.getItem(TRACKING_SESSION_KEY);
    if (state === '1' || state === 'pending') return;
    sessionStorage.setItem(TRACKING_SESSION_KEY, 'pending');
  } catch {
    // Continue without the dedupe marker when storage is unavailable.
  }

  const sessionId = getSessionId();
  const attribution = readAttribution();
  const referrerHost = getReferrerHost();

  fetch(NATURA_TRACKING_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSD-Consent': 'analytics-v1' },
    body: JSON.stringify({
      campaign_id: NATURA_TRACKING_CAMPAIGN_ID,
      source: 'couteau_suisse_digital',
      source_type: resolveSourceType(attribution.utm_source),
      pathname,
      entry_host: window.location.hostname,
      referrer: safeUrl(document.referrer),
      referrer_host: referrerHost,
      external_id: sessionId,
      utm_source: attribution.utm_source || null,
      utm_medium: attribution.utm_medium || null,
      utm_campaign: attribution.utm_campaign || null,
      utm_content: attribution.utm_content || null,
      utm_term: attribution.utm_term || null,
      lang: (navigator.language || 'fr').split('-')[0].slice(0, 8),
      device_type:
        window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1200 ? 'tablet' : 'desktop',
      visitor_stage: 'page_load',
    }),
    keepalive: true,
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Natura visitor tracking failed: ${response.status}`);
      try {
        sessionStorage.setItem(TRACKING_SESSION_KEY, '1');
      } catch {
        // No-op.
      }
    })
    .catch(() => {
      try {
        sessionStorage.removeItem(TRACKING_SESSION_KEY);
      } catch {
        // No-op. Analytics failure must never block the site.
      }
    });
};

export const trackNaturaBusinessEvent = (
  action: string,
  metadata: BusinessMetadata = {}
) => {
  if (typeof window === 'undefined' || !hasCsdAnalyticsConsent()) return;

  const pathname = normalizedPath(window.location.pathname);
  if (!pathname.startsWith('/') || protectedPath(pathname)) return;

  const sessionId = getSessionId();

  fetch(NATURA_UX_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSD-Consent': 'analytics-v1' },
    body: JSON.stringify({
      campaign_id: NATURA_TRACKING_CAMPAIGN_ID,
      session_id: sessionId,
      event_type: 'click',
      page_url: `${window.location.origin}${pathname}`,
      path: pathname,
      language: (navigator.language || 'fr').split('-')[0].slice(0, 8),
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      target_text: action.slice(0, 240),
      target_selector: '[data-natura-business-event]',
      metadata: sanitizeMetadata({
        site: 'couteau_suisse_digital',
        action: action.slice(0, 120),
        ...metadata,
      }),
    }),
    keepalive: true,
  }).catch(() => {
    // Analytics must never break a commercial action.
  });
};
